import { useCallback, useEffect, KeyboardEvent } from 'react';
import type { SelectorOption } from '@/components/ui/inputs/selector';


export interface UseSelectorKeyboardOptions {
  open: boolean;
  disabled: boolean;
  filtered: SelectorOption[];
  activeIndex: number;
  focusSelectedIndex: number;
  listRef: React.RefObject<HTMLUListElement | null>;
  setActiveIndex: (index: number) => void;
  toggleValue: (value: string) => void;
  openMenu: () => void;
  closeMenu: () => void;
  /** Current search text. Resets activeIndex only when the user types,
   * not when selection reorders the list. */
  search?: string;
}

export interface UseSelectorKeyboardReturn {
  handleTriggerKey: (event: KeyboardEvent<HTMLButtonElement>) => void;
  handleListKey: (event: KeyboardEvent<HTMLUListElement>) => void;
}

/**
 * Hook to manage keyboard navigation for Selector
 */
export function useSelectorKeyboard({
  open,
  disabled,
  filtered,
  activeIndex,
  focusSelectedIndex,
  listRef,
  setActiveIndex,
  toggleValue,
  openMenu,
  closeMenu,
  search = '',
}: UseSelectorKeyboardOptions): UseSelectorKeyboardReturn {
  
  const moveActive = useCallback((delta: number) => {
    if (!filtered.length) return;
    let next = activeIndex;
    if (next === -1) next = focusSelectedIndex >= 0 ? focusSelectedIndex : 0;
    next = (next + delta + filtered.length) % filtered.length;
    setActiveIndex(next);
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${next}"]`)?.focus();
  }, [filtered.length, activeIndex, focusSelectedIndex, setActiveIndex, listRef]);

  const handleTriggerKey = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (open) {
        closeMenu();
      } else {
        openMenu();
      }
    }
  }, [disabled, open, openMenu, closeMenu]);

  const handleListKey = useCallback((event: KeyboardEvent<HTMLUListElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive(-1);
        break;
      case 'Home':
        event.preventDefault();
        if (filtered.length) {
          setActiveIndex(0);
          listRef.current?.querySelector<HTMLElement>('[data-idx="0"]')?.focus();
        }
        break;
      case 'End':
        event.preventDefault();
        if (filtered.length) {
          const last = filtered.length - 1;
          setActiveIndex(last);
          listRef.current?.querySelector<HTMLElement>(`[data-idx="${last}"]`)?.focus();
        }
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (activeIndex >= 0 && filtered[activeIndex] && !filtered[activeIndex].disabled) {
          toggleValue(filtered[activeIndex].value);
        }
        break;
      }
      case 'Escape':
        event.preventDefault();
        closeMenu();
        break;
      case 'Tab':
        closeMenu();
        break;
    }
  }, [moveActive, filtered, activeIndex, setActiveIndex, listRef, toggleValue, closeMenu]);

  // Reset active index only when the search text changes, not when selection
  // reorders the filtered list. In multi-select, toggling an option changes
  // the filtered reference (selected options bubble to top) but the user's
  // keyboard position should survive that reorder.
  useEffect(() => {
    setActiveIndex(-1);
  }, [search, setActiveIndex]);

  return {
    handleTriggerKey,
    handleListKey,
  };
}
